<template>
  <section class="section content-container">
    <n-loading-spinner v-if="isLoading" />
    <div class="container is-fluid" v-if="!isLoading">
      <div class="columns">
        <div class="column">
          <TitledSection class="container is-fluid island">
            <template v-slot:title>Workspace</template>
            <template v-slot:subtitle>Change your workspace name</template>

            <div class="island-body">
              <div class="columns">
                <div class="column is-12">
                  <label class="label">Workspace ID</label>
                  <ReadonlyCopyableField :readonlyValue="currentWorkspace.id" onCopyMessage="Copied to clipboard" />

                  <label class="label" for="name">Workspace name</label>
                  <div class="control">
                    <n-text-input
                      id="name"
                      v-model="workspaceName"
                      :errorMessage="getErrorMessage('workspaceName', 'Name')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TitledSection>

          <TitledSection class="container is-fluid island">
            <template v-slot:title>Retention Policies</template>
            <template v-slot:subtitle>Define how long you want to retain your customer's data</template>

            <div class="island-body">
              <div class="columns">
                <div class="column is-12">
                  <n-number-input
                    id="messageRetentionInDays"
                    v-model="messageRetentionInDays"
                    label="Messages (in days)"
                    :min="1"
                    :max="365"
                    :errorMessage="getErrorMessage('messageRetentionInDays', 'Retention policy')"
                  />
                  <br />
                  <n-number-input
                    id="appUserRetentionInDays"
                    v-model="appUserRetentionInDays"
                    label="Inactive leads (in days)"
                    :min="1"
                    :max="365"
                    :errorMessage="getErrorMessage('appUserRetentionInDays', 'Retention policy')"
                  />
                </div>
              </div>
            </div>
          </TitledSection>

          <TitledSection class="container is-fluid island" v-if="showUsageLimits">
            <template v-slot:title>Usage Limits</template>
            <template v-slot:subtitle>Set usage limits for proactive triggers</template>

            <div class="island-body">
              <div class="columns">
                <div class="column is-12">
                  <n-number-input
                    id="max-proactive-triggers"
                    v-model="maxProactiveTriggers"
                    label="Proactive triggers (per month)"
                    :min="0"
                    :errorMessage="getErrorMessage('maxProactiveTriggers', 'Usage limit')"
                  />
                </div>
              </div>
            </div>
          </TitledSection>

          <n-button @click="updateWorkspace()" variant="primary" :loading="updateInProgress" :disabled="$v.$invalid">
            Save
          </n-button>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { workspacesApi } from '@/workspaces/workspaces';
import Toast from '@/shared/services/toast';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import TitledSection from '@/shared/components/TitledSection.vue';
import { messagingWorkspacesApi } from '@/messaging/messaging';
import { AddOrReplaceMessagingWorkspaceRequest } from '@/messaging/models/addOrReplaceMessagingWorkspaceRequest';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { UsageLimitType } from '@/features/settings/models/usageLimitType';
import { UpdateWorkspaceViewModel } from '@/workspaces/models/request/updateWorkspaceViewModel';
import { MAX_INTEGER } from '@/shared/constants';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';
import { useStore } from 'vuex';
import { computed, onMounted, reactive, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minValue, maxValue } from '@vuelidate/validators';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import { useVuelidateTouchWatcher } from '@shared/util/useVuelidateTouchWatcher';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const workspaceUsageLimit = computed(() => store.getters.workspaceUsageLimit);
const isWorkspaceFeatureEnabled = computed(() => store.getters.isWorkspaceFeatureEnabled);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const workspaceName = ref('');
const messageRetentionInDays = ref(90);
const appUserRetentionInDays = ref(365);
const maxProactiveTriggers = ref(0);
const updateInProgress = ref(false);
const isLoading = ref(true);
const showUsageLimits = ref(false);

const validations = {
  workspaceName: { required },
  messageRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
  appUserRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
  maxProactiveTriggers: { required, minValue: minValue(0), maxValue: maxValue(MAX_INTEGER) },
};

const $v = useVuelidate(validations, {
  workspaceName,
  messageRetentionInDays,
  appUserRetentionInDays,
  maxProactiveTriggers,
});

onMounted(async () => {
  try {
    if (isWorkspaceFeatureEnabled.value(PROACTIVE_FLOWS_ENABLED)) {
      showUsageLimits.value = true;
    }
    workspaceName.value = currentWorkspace.value.name;
    const workspaceFromMessaging = await messagingWorkspacesApi.getWorkspace(currentWorkspace.value.id);
    messageRetentionInDays.value = workspaceFromMessaging.messageRetentionInDays;
    appUserRetentionInDays.value = workspaceFromMessaging.appUserRetentionInDays;
    maxProactiveTriggers.value = workspaceUsageLimit.value(UsageLimitType.ProactiveTriggers);
  } catch (error: any) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    } else {
      messageRetentionInDays.value = 90;
      appUserRetentionInDays.value = 365;
    }
  } finally {
    isLoading.value = false;

    useVuelidateTouchWatcher($v, [
      ['workspaceName', workspaceName],
      ['messageRetentionInDays', messageRetentionInDays],
      ['appUserRetentionInDays', appUserRetentionInDays],
      ['maxProactiveTriggers', maxProactiveTriggers],
    ]);
  }
});

async function updateWorkspace() {
  if (!updateInProgress.value) {
    try {
      updateInProgress.value = true;
      const request = createUpdateRequest();
      await workspacesApi.update(currentWorkspace.value.id, request);
      currentWorkspace.value.name = request.name;
      currentWorkspace.value.usageLimits = request.usageLimits;

      await messagingWorkspacesApi.addOrReplace(createAddOrReplaceMessagingWorkspaceRequest());

      Toast.showSuccess(`Successfully updated workspace ${currentWorkspace.value.name}`);
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      updateInProgress.value = false;
    }
  }
}

function getErrorMessage(prop: string, label: string) {
  return VuelidateUtil.getErrorMessage($v, prop, label);
}

function createAddOrReplaceMessagingWorkspaceRequest(): AddOrReplaceMessagingWorkspaceRequest {
  return {
    id: currentWorkspace.value.id,
    name: currentWorkspace.value.name,
    messageRetentionInDays: messageRetentionInDays.value,
    appUserRetentionInDays: appUserRetentionInDays.value,
  };
}

function createUpdateRequest() {
  if (showUsageLimits.value) {
    return {
      name: workspaceName.value,
      usageLimits: {
        [UsageLimitType.ProactiveTriggers]: maxProactiveTriggers.value,
      },
    } as UpdateWorkspaceViewModel;
  } else {
    return {
      name: workspaceName.value,
    } as UpdateWorkspaceViewModel;
  }
}
</script>

<style scoped lang="scss">
.island {
  min-height: 180px;
}

.island-body {
  flex: 1;
}

.domain-key-column {
  width: 100px;
}

.label {
  margin-top: 1rem;

  &:first-of-type {
    margin-top: 0;
  }
}

:deep(input#max-proactive-triggers),
:deep(input#messageRetentionInDays),
:deep(input#appUserRetentionInDays) {
  width: 5rem;
}

section {
  flex: 1;
}
</style>
